import { ArticleLayout } from 'components/Article/ArticleLayout';
import { sanityCategoryIds } from 'constants/sanityCategoryIds';
import { alternateSlugsQuery, includeDrafts } from 'constants/sanityQueries';
import { findLocaleBySanityLanguage, findSanityLanguageByLocale } from 'helpers/language';
import { generateAlternateSlugFromSanity } from 'helpers/url';
import { getClient } from 'lib/sanity/sanity.server';
import { GetStaticPaths, GetStaticProps } from 'next';
import { SanityArticleDataWithAuthor, SanitySlug } from 'types/sanity';

const BlogArticlePage = ({ article }: { article: SanityArticleDataWithAuthor }) => (
  <ArticleLayout data={article} />
);

export const getStaticProps: GetStaticProps = async ({ locale = '', params, preview = false }) => {
  const article = await getClient(preview).fetch(
    `${
      preview
        ? '*[_type == "post" && slug.current == $slug && references($categoryId)]'
        : '*[_type == "post" && __i18n_lang == $locale && slug.current == $slug && references($categoryId)]'
    } | order(_updatedAt desc)[0] {
      ...,
      author->,
      ${alternateSlugsQuery}
    }`,
    {
      locale: findSanityLanguageByLocale(locale),
      slug: params?.slug,
      categoryId: sanityCategoryIds['article'],
    },
  );

  if (!article) {
    return {
      notFound: true,
    };
  }

  const alternateSlugs = generateAlternateSlugFromSanity(article?.alternateSlugs, params) ?? [];

  return {
    props: { article, alternateSlugs: alternateSlugs },
    revalidate: 600,
  };
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const pages = await getClient(false).fetch(
    `*[_type == "post" && references($categoryId) ${includeDrafts(
      false,
    )}] { "slug": slug, "lang": __i18n_lang }`,
    {
      categoryId: sanityCategoryIds['article'],
    },
  );

  return {
    paths: pages
      ?.filter((page: { slug: SanitySlug; lang: string }) =>
        locales?.includes(findLocaleBySanityLanguage(page.lang)),
      )
      ?.filter((page: { slug: SanitySlug; lang: string }) => page.slug.current)
      .map((page: { slug: SanitySlug; lang: string }) => ({
        params: { slug: page.slug.current },
        locale: findLocaleBySanityLanguage(page.lang),
      })),
    fallback: true,
  };
};

export default BlogArticlePage;
